/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

/** 
  All of the routes for the Vision UI Dashboard React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Vision UI Dashboard React layouts
import Dashboard from "layouts/dashboard";
import Profile from "layouts/admin/myProfile";

// Vision UI Dashboard React icons

import { IoHome } from "react-icons/io5";
import Clients from "layouts/clients";
import {
  FaAddressBook,
  FaBroom,
  FaClipboardCheck,
  FaClipboardList,
  FaHouseUser,
  FaRegUserCircle,
  FaSmileBeam,
  FaTools,
  FaUserCircle,
  FaUsers,
  FaWrench,
} from "react-icons/fa";
import { FaArrowRightArrowLeft, FaGears, FaUserGear } from "react-icons/fa6";
import Roles from "layouts/admin/roles";
import Users from "layouts/admin/myUsers";
import Arrivals from "layouts/admin/arrivals";
import Integrations from "layouts/integrations";
import AdminIntegrations from "layouts/admin/integrationSettings";
import InHouseGuest from "layouts/admin/in-house";
import Requests from "layouts/admin/requests";
import GuestLibrary from "layouts/admin/guestLibrary";
import ProfileHistory from "layouts/admin/guestLibrary/components/ProfileHistoryScreen";
import RemovedGuests from "layouts/admin/guestLibrary/components/RemovedGuestsScreen";
import DailyOperationsHub from "layouts/admin/operationsHub";
import DeletedOperationsTemplates from "layouts/admin/operationsHub/components/DeletedOperationsTemplates";
import MaintenanceHub from "layouts/admin/maintenanceHub";
import GuestSatisfactionHub from "layouts/admin/guestSatisfactionHub";
import HousekeepingHub from "layouts/admin/housekeeping";

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    route: "/dashboard",
    icon: <IoHome size="15px" color="inherit" />,
    component: <Dashboard />,
    permission: "viewDashboard",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Clients",
    key: "clients",
    route: "/clients",
    icon: <FaUsers size="15px" color="inherit" />,
    component: <Clients />,
    permission: "viewAdmins",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Integrations",
    key: "integrations",
    route: "/integrations",
    icon: <FaGears size="17px" color="inherit" />,
    component: <Integrations />,
    permission: "viewIntegrationProviders",
    superAdminOnly: true,
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Daily Operations",
    key: "daily-operations",
    route: "/daily-operations",
    icon: <FaClipboardCheck size="14px" color="inherit" />,
    component: <DailyOperationsHub />,
    permission: "viewOperations",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Deleted Templates",
    key: "daily-operations",
    route: "/daily-operations/deleted-templates",
    icon: <FaClipboardCheck size="14px" color="inherit" />,
    component: <DeletedOperationsTemplates />,
    permission: "viewOperations",
    protected: true,
    noCollapse: true,
    showInSidenav: false,
  },

  {
    type: "collapse",
    name: "Arrivals & Departures",
    key: "arrivals&departures",
    route: "/arrivals&departures",
    icon: <FaArrowRightArrowLeft size="14px" color="inherit" />,
    component: <Arrivals />,
    permission: "viewArrivalDeparture",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "In-House Guests",
    key: "inHouseGuests",
    route: "/inHouseGuests",
    icon: <FaHouseUser size="14px" color="inherit" />,
    component: <InHouseGuest />,
    permission: "viewInHouseGuests",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Requests",
    key: "requests",
    route: "/requests",
    icon: <FaClipboardList size="14px" color="inherit" />,
    component: <Requests />,
    permission: "viewRequests",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Housekeeping Hub",
    key: "housekeepingHub",
    route: "/housekeepingHub",
    icon: <FaBroom size="14px" />,
    component: <HousekeepingHub />,
    permission: "viewHouseKeeping",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Maintenance Hub",
    key: "maintenance-hub",
    route: "/maintenance-hub",
    icon: <FaTools size="14px" color="inherit" />,
    component: <MaintenanceHub />,
    permission: "viewMaintenance",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Guest Library",
    key: "guestLibrary",
    route: "/guestLibrary",
    icon: <FaAddressBook size="14px" color="inherit" />,
    component: <GuestLibrary />,
    permission: "viewGuests",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Profile History",
    key: "profile-history",
    route: "/guestLibrary/profile-history/:guestId",
    component: <ProfileHistory />,
    permission: "viewGuests",
    protected: true,
    noCollapse: true,
    showInSidenav: false,
  },

  {
    type: "collapse",
    name: "Removed Guests",
    key: "removed-guests",
    route: "/guestLibrary/removed",
    component: <RemovedGuests />,
    permission: "viewGuests",
    protected: true,
    noCollapse: true,
    showInSidenav: false,
  },

  {
    type: "collapse",
    name: "Guest Satisfaction",
    key: "satisfactionHub",
    route: "/satisfactionHub",
    icon: <FaSmileBeam size="14px" />,
    component: <GuestSatisfactionHub />,
    permission: "viewSatisfactionForm",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Roles",
    key: "roles",
    route: "/roles",
    icon: <FaUserGear size="15px" color="inherit" />,
    component: <Roles />,
    permission: "viewRoles",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "My users",
    key: "users",
    route: "/users",
    icon: <FaUsers size="15px" color="inherit" />,
    component: <Users />,
    permission: "viewUsers",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Manage Integrations",
    key: "manage-integrations",
    route: "/manage-integrations",
    icon: <FaGears size="15px" color="inherit" />,
    component: <AdminIntegrations />,
    permission: "viewIntegrations",
    protected: true,
    noCollapse: true,
  },

  {
    type: "collapse",
    name: "Profile",
    key: "profile",
    route: "/profile",
    icon: <FaUserCircle size="15px" color="inherit" />,
    component: <Profile />,
    // permission: "viewProfile",
    noCollapse: true,
    protected: true,
  },
];

export default routes;
